<template>
  <b-form @submit.prevent="submit">
    <h4 class="mb-2">
      {{ isEdit ? $t('usersettings.editRole') : $t('usersettings.createNewRole') }}
    </h4>

    <b-form-group :label="$t('usersettings.roleNameDisplay')">
      <b-form-input
        v-model="form.display_name"
        :placeholder="$t('usersettings.eGComplianceOfficer')"
        required
      />
    </b-form-group>

    <h5 class="mt-2 mb-1">
      {{ $t('usersettings.permissionsMatrix') }}
    </h5>

    <div
      v-for="(group, category) in categorizedPermissions"
      :key="category"
      class="mb-2 p-1 border rounded"
    >
      <div class="d-flex justify-content-between align-items-center mb-1">
        <h6 class="text-primary font-weight-bolder text-uppercase mb-0">
          {{ systemLabel(category) }}
        </h6>
        <b-link
          size="sm"
          @click="toggleCategory(category)"
        >
          <small>{{ $t('usersettings.toggleAll') }}</small>
        </b-link>
      </div>

      <b-row>
        <b-col
          v-for="permission in group"
          :key="permission.name"
          cols="12"
          md="6"
          lg="4"
        >

          <b-form-checkbox
            v-model="form.permissions"
            :value="permission.name"
            class="mb-50 custom-control-primary"
          >
            {{ permissionLabel(permission.name, permission.display_name) }}
          </b-form-checkbox>
        </b-col>
      </b-row>
    </div>

    <div class="d-flex justify-content-end mt-3">
      <b-button
        type="submit"
        variant="primary"
      >
        {{ isEdit ? $t('usersettings.updateRole') : $t('usersettings.createRole') }}
      </b-button>
    </div>
  </b-form>
</template>

<script>
import i18n from '@/libs/i18n'
import { systemLabel, permissionLabel } from '@/libs/i18n/display'

export default {
  props: {
    roleData: {
      type: Object,
      default: () => ({}),
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      allPermissions: [],
      form: {
        display_name: '',
        permissions: [],
      },
    }
  },
  computed: {
    // Dynamically group permissions based on their names
    categorizedPermissions() {
      const groups = {
        Apps: [],
        Users: [],
        Reports: [],
        Logs: [],
        Other: [],
      }

      this.allPermissions.forEach(p => {
        const name = p.name.toLowerCase()
        if (name.includes('app')) groups.Apps.push(p)
        else if (name.includes('user')) groups.Users.push(p)
        else if (name.includes('logs')) groups.Logs.push(p)
        else if (name.includes('report') || name.includes('stat') || name.includes('revenue')) groups.Reports.push(p)
        else groups.Other.push(p)
      })

      // Clean up empty categories
      return Object.fromEntries(Object.entries(groups).filter(([, v]) => v.length > 0))
    },
  },
  watch: {
    roleData: {
      immediate: true,
      handler(roleData) {
        this.form = {
          display_name: roleData?.display_name || '',
          permissions: [...(roleData?.permissions || [])],
        }
      },
    },
  },
  created() {
    this.fetchPermissions()
  },
  methods: {
    systemLabel,
    permissionLabel,
    fetchPermissions() {
      this.$http.get('/api/publisher-roles/permissions').then(res => {
        this.allPermissions = res.data
      }).catch(errors => {
        this.allPermissions = []
        this.alertApiError(errors, i18n.t('usersettings.failedToLoadPermissions'))
      })
    },
    // Toggle all permissions in a specific category
    toggleCategory(category) {
      const categoryPerms = this.categorizedPermissions[category].map(p => p.name)
      const allSelected = categoryPerms.every(p => this.form.permissions.includes(p))

      if (allSelected) {
        this.form.permissions = this.form.permissions.filter(p => !categoryPerms.includes(p))
      } else {
        const unique = new Set([...this.form.permissions, ...categoryPerms])
        this.form.permissions = Array.from(unique)
      }
    },
    submit() {
      const payload = {
        name: this.form.display_name,
        permissions: this.form.permissions,
      }

      const url = this.isEdit ? `/api/publisher-roles/${this.roleData.id}` : '/api/publisher-roles'
      const method = this.isEdit ? 'put' : 'post'

      this.showLoading()
      this.$http[method](url, payload).then(response => {
        if (response.data.success) {
          this.$emit('saved')
          this.alertSuccess(this.isEdit ? i18n.t('usersettings.roleUpdated') : i18n.t('usersettings.roleCreated'))
        }
      })
        .catch(errors => {
          this.alertApiError(errors, this.isEdit ? i18n.t('usersettings.roleUpdateFailed') : i18n.t('usersettings.roleCreationFailed'))
        })
        .finally(() => {
          this.hideLoading()
        })
    },
  },
}
</script>
